import { useEffect, useMemo, useRef, useState } from "react";
import { createHistoryStore, type HistoryStore } from "./positionHistory";
import {
  buildRouteProjection,
  type RawRouteGeometry,
  type RouteProjection,
} from "./routeProjection";

// Live Stringline diagram: one chart per route. X axis = time (last `windowSec`
// seconds, right edge = now). Y axis = distance along route, with stops
// drawn as horizontal gridlines and labeled on the left. Each vehicle is
// drawn as a polyline through its history samples (projected onto the
// route's canonical polyline).
//
// Trains moving outbound show as one diagonal slope; inbound trains slope
// the opposite way. A turnaround at a terminus shows as a "V" — that's
// the diagram doing its job, not a bug.

const WINDOW_SEC = 30 * 60; // 30 minutes
const GAP_BREAK_SEC = 180; // break a vehicle's polyline if it goes silent this long
// Stable empty store reference so React effects don't see a fresh identity
// every render while we're loading a historical snapshot.
const EMPTY_HISTORY_STORE: HistoryStore = createHistoryStore();

// Hardcoded panel layout: one chart per (route, terminus). The headsign
// match is case-insensitive substring against the trip headsign reported
// by OBA. Trains whose headsign matches neither are dropped from both
// panels (they're usually deadheads or short-turns).
export type PanelSpec = {
  routeId: string;
  terminus: string;
  headsignMatch: string[];
};

export const PANELS: PanelSpec[] = [
  { routeId: "40_100479", terminus: "Lynnwood", headsignMatch: ["lynnwood"] },
  {
    routeId: "40_100479",
    terminus: "Federal Way",
    headsignMatch: ["federal way", "angle lake"],
  },
  { routeId: "40_2LINE", terminus: "Lynnwood", headsignMatch: ["lynnwood"] },
  {
    routeId: "40_2LINE",
    terminus: "Redmond",
    headsignMatch: ["redmond"],
  },
];

export function matchesPanel(headsign: string | undefined, panel: PanelSpec): boolean {
  if (!headsign) return false;
  const h = headsign.toLowerCase();
  return panel.headsignMatch.some((m) => h.includes(m));
}

type Props = {
  history: HistoryStore;
  /** Bumped by the parent each time history changes; drives re-render. */
  historyVersion: number;
  /** Connection state — surfaced as a small status indicator. */
  connected: boolean;
  /**
   * When non-null, the Stringline panels render the WINDOW_SEC ending at this
   * unix-seconds timestamp using a backend-fetched historical sample set
   * instead of the live in-memory store. The time axis is also anchored
   * to this instant so "now" on the chart means "the scrubbed moment".
   */
  anchorTs?: number | null;
};

export function StringlineDiagram({
  history,
  historyVersion,
  connected,
  anchorTs = null,
}: Props) {
  // Historical sample buffer fetched from /vehicles/history when scrubbing.
  // Kept separate from the live store so flipping back to live is instant.
  const [historicalStore, setHistoricalStore] = useState<HistoryStore | null>(
    null,
  );
  const [historicalVersion, setHistoricalVersion] = useState(0);
  const [historicalLoading, setHistoricalLoading] = useState(false);

  useEffect(() => {
    if (anchorTs === null) {
      setHistoricalStore(null);
      setHistoricalLoading(false);
      return;
    }
    setHistoricalLoading(true);
    const ctl = new AbortController();
    // Debounce so dragging the slider doesn't fire a request per frame.
    const id = window.setTimeout(async () => {
      try {
        const res = await fetch(
          `/api/realtime/vehicles/history?to=${encodeURIComponent(
            String(anchorTs),
          )}&window=${WINDOW_SEC}`,
          { signal: ctl.signal },
        );
        if (!res.ok) return;
        const data = (await res.json()) as {
          vehicles?: Array<{
            vehicle_id: string;
            route_id?: string;
            route_short_name?: string;
            route_color?: string;
            samples: Array<{
              ts: number;
              lat: number;
              lon: number;
              bearing?: number;
              trip_id?: string;
              trip_headsign?: string;
            }>;
          }>;
        };
        const store = createHistoryStore();
        for (const v of data.vehicles ?? []) {
          if (!v.vehicle_id || !v.route_id) continue;
          store.byVehicle.set(v.vehicle_id, {
            vehicleId: v.vehicle_id,
            routeId: v.route_id,
            routeColor: v.route_color,
            routeShortName: v.route_short_name,
            samples: (v.samples ?? []).map((s) => ({
              ts: s.ts,
              lat: s.lat,
              lon: s.lon,
              bearing: s.bearing,
              routeId: v.route_id ?? "",
              routeColor: v.route_color,
              routeShortName: v.route_short_name,
              tripId: s.trip_id,
              tripHeadsign: s.trip_headsign,
            })),
          });
        }
        setHistoricalStore(store);
        setHistoricalVersion((n) => n + 1);
      } catch {
        // AbortError or network blip — next slider movement will retry.
      } finally {
        setHistoricalLoading(false);
      }
    }, 120);
    return () => {
      window.clearTimeout(id);
      ctl.abort();
    };
  }, [anchorTs]);

  const isHistorical = anchorTs !== null;
  // While the historical fetch is in flight (or hasn't been triggered yet),
  // render an empty store rather than falling back to the live history —
  // the live samples have timestamps from "now" and would otherwise show
  // up at the wrong end of the past time axis (or nowhere at all),
  // making the chart look empty during a scrub. An empty store + the
  // existing "Loading historical data…" overlay below is the honest UX.
  const activeHistory = isHistorical
    ? historicalStore ?? EMPTY_HISTORY_STORE
    : history;
  const activeVersion = isHistorical ? historicalVersion : historyVersion;
  const [projections, setProjections] = useState<RouteProjection[] | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);

  // Tick once a second so the time axis (which is anchored at "now")
  // scrolls smoothly even if no new SSE snapshot arrived. When viewing
  // history, freeze "now" at the anchor instant so the chart doesn't
  // drift while the user inspects a moment.
  const [liveNow, setLiveNow] = useState(() => Math.floor(Date.now() / 1000));
  useEffect(() => {
    if (isHistorical) return;
    const id = window.setInterval(
      () => setLiveNow(Math.floor(Date.now() / 1000)),
      1000,
    );
    return () => window.clearInterval(id);
  }, [isHistorical]);
  const now = isHistorical ? (anchorTs as number) : liveNow;

  // Fetch structured route geometry once; cache projections in state.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch("/api/routes/geometry");
        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`);
        }
        const data = (await res.json()) as RawRouteGeometry[];
        if (cancelled) return;
        const projs = data
          .map(buildRouteProjection)
          .filter((p) => p.lengthM > 0);
        // Stable sort by short name so the 1 Line shows up before the 2 Line.
        projs.sort((a, b) =>
          a.shortName.localeCompare(b.shortName, undefined, { numeric: true }),
        );
        setProjections(projs);
      } catch (e) {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : String(e));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Build one set of tracks per (route, terminus) panel. We keep only
  // vehicles whose latest known headsign matches the panel's terminus,
  // so the two directions of each line don't cross-hatch into the same
  // chart.
  const panelData = useMemo(() => {
    if (!projections) return [];
    const projByRoute = new Map(projections.map((p) => [p.routeId, p]));
    return PANELS.map((panel) => {
      const proj = projByRoute.get(panel.routeId);
      if (!proj) return { panel, projection: null, tracks: [] };
      const tracks = projectVehicles(activeHistory, proj, panel);
      return { panel, projection: proj, tracks };
    });
    // activeVersion is the actual mutation signal here; activeHistory is a
    // stable ref-backed object whose identity changes only when we swap
    // between live and historical stores.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeVersion, activeHistory, projections]);

  if (error) {
    return (
      <div className="stringline-empty">
        Couldn't load route geometry: {error}
      </div>
    );
  }
  if (!projections) {
    return <div className="stringline-empty">Loading route geometry…</div>;
  }
  if (projections.length === 0) {
    return <div className="stringline-empty">No routes available.</div>;
  }

  return (
    <div className="stringline">
      <div className="stringline-header">
        <div className="stringline-title">
          Stringline · last 30 min
          {isHistorical && (
            <span className="stringline-history-tag">
              {" "}· @ {formatHistoryClock(anchorTs as number)}
            </span>
          )}
        </div>
        <div
          className={`stringline-status ${
            isHistorical ? "history" : connected ? "ok" : "wait"
          }`}
        >
          {isHistorical
            ? historicalLoading
              ? "○ loading history…"
              : "◐ history"
            : connected
              ? "● live"
              : "○ waiting…"}
        </div>
      </div>
      <div className="stringline-charts">
        {panelData.map(({ panel, projection, tracks }) =>
          projection ? (
            <RouteChart
              key={`${panel.routeId}-${panel.terminus}`}
              projection={projection}
              panel={panel}
              tracks={tracks}
              now={now}
            />
          ) : (
            <div
              key={`${panel.routeId}-${panel.terminus}`}
              className="stringline-chart stringline-chart-missing"
            >
              <div className="stringline-chart-title">
                {panel.routeId} → {panel.terminus}
                <span className="stringline-chart-meta">no geometry</span>
              </div>
            </div>
          ),
        )}
      </div>
    </div>
  );
}

export type VehicleTrack = {
  vehicleId: string;
  /** Each segment is a contiguous run of samples, broken on long gaps. */
  segments: { ts: number; distM: number }[][];
  /** Most recent sample (for the "head" dot). */
  head: { ts: number; distM: number } | null;
  routeShortName?: string;
  tripHeadsign?: string;
};

export function projectVehicles(
  history: HistoryStore,
  proj: RouteProjection,
  panel: PanelSpec,
): VehicleTrack[] {
  const tracks: VehicleTrack[] = [];
  for (const entry of history.byVehicle.values()) {
    if (entry.routeId !== proj.routeId) continue;

    // Walk samples and only keep contiguous runs whose headsign matches
    // this panel. A vehicle that turns around at a terminus naturally
    // splits into two trips; each ends up on the appropriate chart.
    const segments: { ts: number; distM: number }[][] = [];
    let cur: { ts: number; distM: number }[] = [];
    let lastTs = -Infinity;
    let matchedAny = false;
    let lastMatchingHeadsign: string | undefined;

    const flush = () => {
      if (cur.length > 0) {
        segments.push(cur);
        cur = [];
      }
    };

    for (const s of entry.samples) {
      if (!matchesPanel(s.tripHeadsign, panel)) {
        flush();
        lastTs = -Infinity;
        continue;
      }
      const distM = proj.project(s.lat, s.lon);
      if (distM == null) {
        flush();
        lastTs = -Infinity;
        continue;
      }
      if (s.ts - lastTs > GAP_BREAK_SEC) flush();
      cur.push({ ts: s.ts, distM });
      lastTs = s.ts;
      matchedAny = true;
      lastMatchingHeadsign = s.tripHeadsign;
    }
    flush();

    if (!matchedAny || segments.length === 0) continue;
    const lastSeg = segments[segments.length - 1];
    const head = lastSeg.length > 0 ? lastSeg[lastSeg.length - 1] : null;
    tracks.push({
      vehicleId: entry.vehicleId,
      segments,
      head,
      routeShortName: entry.routeShortName,
      tripHeadsign: lastMatchingHeadsign,
    });
  }
  return tracks;
}

function RouteChart({
  projection,
  panel,
  tracks,
  now,
}: {
  projection: RouteProjection;
  panel: PanelSpec;
  tracks: VehicleTrack[];
  now: number;
}) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [size, setSize] = useState({ w: 0, h: 0 });
  const [hover, setHover] = useState<{
    vehicleId: string;
    routeShortName?: string;
    tripHeadsign?: string;
    distM: number;
    ts: number;
    x: number;
    y: number;
  } | null>(null);

  useEffect(() => {
    if (!containerRef.current) return;
    const el = containerRef.current;
    const ro = new ResizeObserver((entries) => {
      for (const ent of entries) {
        const cr = ent.contentRect;
        setSize({ w: Math.floor(cr.width), h: Math.floor(cr.height) });
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const W = size.w;
  const H = size.h;
  const M = { top: 28, right: 16, bottom: 26, left: 150 };
  const innerW = Math.max(W - M.left - M.right, 0);
  const innerH = Math.max(H - M.top - M.bottom, 0);

  const tStart = now - WINDOW_SEC;
  const tEnd = now;

  // Coordinate transforms. Distance is mapped top->bottom so the first stop
  // (distM=0) appears at the top — matches how riders read schedules and
  // is the convention used by historical Stringline diagrams.
  const xOf = (ts: number) =>
    M.left + ((ts - tStart) / (tEnd - tStart)) * innerW;
  const yOf = (distM: number) =>
    M.top + (distM / Math.max(projection.lengthM, 1)) * innerH;

  // Time tick marks every 5 minutes, anchored to the present so they
  // visibly march left as time advances.
  const ticks: { ts: number; label: string }[] = [];
  const tickStep = 300;
  const firstTick = Math.ceil(tStart / tickStep) * tickStep;
  for (let t = firstTick; t <= tEnd; t += tickStep) {
    const minsAgo = Math.round((tEnd - t) / 60);
    ticks.push({
      ts: t,
      label: minsAgo === 0 ? "now" : `-${minsAgo}m`,
    });
  }

  // Render every stop. Labels can crowd on short charts; CSS handles the
  // visual density.
  const renderedStops = projection.stops;

  const pathFor = (seg: { ts: number; distM: number }[]): string => {
    // Include the sample immediately preceding the visible window (if any)
    // so the polyline enters from the left edge instead of starting mid-air
    // at the first sample inside the window. SVG clipping will trim it
    // cleanly — except we deliberately don't clip, so the path can briefly
    // extend into the left margin and that's fine.
    let firstIdx = 0;
    for (let i = 0; i < seg.length; i++) {
      if (seg[i].ts >= tStart) {
        firstIdx = Math.max(0, i - 1);
        break;
      }
      if (i === seg.length - 1) firstIdx = i;
    }
    let d = "";
    for (let i = firstIdx; i < seg.length; i++) {
      const s = seg[i];
      d +=
        (d ? "L" : "M") + xOf(s.ts).toFixed(1) + "," + yOf(s.distM).toFixed(1);
    }
    return d;
  };

  return (
    <div
      className="stringline-chart"
      ref={containerRef}
      style={{ height: Math.max(360, projection.stops.length * 22 + 44) }}
    >
      <div className="stringline-chart-title" style={{ color: projection.color }}>
        {projection.shortName || projection.longName}
        <span className="stringline-chart-terminus"> → {panel.terminus}</span>
        <span className="stringline-chart-meta">
          {tracks.length} {tracks.length === 1 ? "train" : "trains"}
        </span>
      </div>
      {W > 0 && H > 0 && (
        <svg
          width={W}
          height={H}
          className="stringline-svg"
          onMouseLeave={() => setHover(null)}
        >
          <defs>
            <clipPath id={`stringline-plot-${projection.routeId}-${panel.terminus}`}>
              <rect x={M.left} y={M.top} width={innerW} height={innerH} />
            </clipPath>
          </defs>

          {/* Frame */}
          <rect
            x={M.left}
            y={M.top}
            width={innerW}
            height={innerH}
            className="stringline-frame"
          />

          {/* Stop gridlines + labels */}
          {renderedStops.map((s) => {
            const y = yOf(s.distM);
            const yText = Math.round(y);
            return (
              <g key={s.id}>
                <line
                  x1={M.left}
                  x2={M.left + innerW}
                  y1={y}
                  y2={y}
                  className="stringline-stop-line"
                />
                <text
                  x={M.left - 6}
                  y={yText}
                  className="stringline-stop-label"
                  textAnchor="end"
                  dominantBaseline="middle"
                >
                  {s.name}
                </text>
              </g>
            );
          })}

          {/* Time tick marks */}
          {ticks.map((t) => {
            const x = xOf(t.ts);
            const xText = Math.round(x);
            return (
              <g key={t.ts}>
                <line
                  x1={x}
                  x2={x}
                  y1={M.top}
                  y2={M.top + innerH}
                  className="stringline-time-line"
                />
                <text
                  x={xText}
                  y={M.top + innerH + 14}
                  className="stringline-time-label"
                  textAnchor="middle"
                >
                  {t.label}
                </text>
              </g>
            );
          })}

          {/* Vehicle tracks */}
          <g clipPath={`url(#stringline-plot-${projection.routeId}-${panel.terminus})`}>
            {tracks.map((t) =>
              t.segments.map((seg, idx) => {
                const d = pathFor(seg);
                if (!d) return null;
                return (
                  <path
                    key={`${t.vehicleId}-${idx}`}
                    d={d}
                    className="stringline-track"
                    stroke={projection.color}
                  />
                );
              }),
            )}
          </g>

          {/* Vehicle "heads" — the right-most point on each train's line */}
          {tracks.map((t) => {
            if (!t.head) return null;
            const x = xOf(t.head.ts);
            const y = yOf(t.head.distM);
            return (
              <circle
                key={`head-${t.vehicleId}`}
                cx={x}
                cy={y}
                r={3.5}
                className="stringline-head"
                fill={projection.color}
                onMouseEnter={() =>
                  setHover({
                    vehicleId: t.vehicleId,
                    routeShortName: t.routeShortName,
                    tripHeadsign: t.tripHeadsign,
                    distM: t.head!.distM,
                    ts: t.head!.ts,
                    x,
                    y,
                  })
                }
                onMouseLeave={() => setHover(null)}
              />
            );
          })}

          {/* Hover tooltip */}
          {hover && (
            <g pointerEvents="none">
              <line
                x1={hover.x}
                x2={hover.x}
                y1={M.top}
                y2={M.top + innerH}
                className="stringline-hover-line"
              />
              <line
                x1={M.left}
                x2={M.left + innerW}
                y1={hover.y}
                y2={hover.y}
                className="stringline-hover-line"
              />
            </g>
          )}
        </svg>
      )}
      {hover && (
        <div
          className="stringline-tooltip"
          style={{
            left: Math.min(hover.x + 8, W - 200),
            top: Math.max(hover.y - 8, M.top),
          }}
        >
          <div className="stringline-tt-title">
            {hover.routeShortName || projection.shortName} · vehicle{" "}
            {hover.vehicleId}
          </div>
          {hover.tripHeadsign && (
            <div className="stringline-tt-sub">→ {hover.tripHeadsign}</div>
          )}
          <div className="stringline-tt-sub">
            updated {Math.max(0, Math.round(now - hover.ts))}s ago
          </div>
        </div>
      )}
    </div>
  );
}

function formatHistoryClock(ts: number): string {
  const d = new Date(ts * 1000);
  const hh = d.getHours().toString().padStart(2, "0");
  const mm = d.getMinutes().toString().padStart(2, "0");
  const ss = d.getSeconds().toString().padStart(2, "0");
  return `${hh}:${mm}:${ss}`;
}
